import customerRideApi from 'api/customerRideApi';
import React, { useEffect, useState } from 'react';
import RideHomeMap from './components/RideHomeMap';
import RideMapPickerSheet from './components/RideMapPickerSheet';
import RidePlaceSearchSheet from './components/RidePlaceSearchSheet';
import RideSavedPlacesSheet from './components/RideSavedPlacesSheet';
import RideScheduledDemandModal from './components/RideScheduledDemandModal';
import MyRideBookings from './components/MyRideBookings';
import RideSearchSection from './components/RideSearchSection';
import RideMessagesView from './components/RideMessagesView';
import RideAppIcon from './components/RideAppIcon';
import { rideErrorMessage } from './rideUtils';
import './CustomerRide.scss';

const SAVED_PLACES_KEY = 'RIDE_SAVED_PLACES';

// This shortcut represents the centre of the former Giao Thuy district, not
// one confirmed post-2025 commune. Keep the administrative pair empty so the
// API validates and prices it from its address/coordinates, exactly like a
// free-text search result. Never attach a guessed/stale commune code here.
export const RECENT_GIAO_THUY_DROPOFF = Object.freeze({
  address: 'Huyện Giao Thủy, Tỉnh Ninh Bình, Việt Nam',
  provinceCode: null,
  communeCode: null,
  latitude: 20.2541,
  longitude: 106.3359,
});

const readSavedPlaces = () => {
  if (typeof window === 'undefined') return [];
  try {
    const saved = JSON.parse(window.localStorage.getItem(SAVED_PLACES_KEY) || '[]');
    return Array.isArray(saved) ? saved.filter((place) => (
      place?.id && place?.label && place?.point?.address
      && Number.isFinite(Number(place.point.latitude))
      && Number.isFinite(Number(place.point.longitude))
    )) : [];
  } catch (_) {
    return [];
  }
};

const CustomerRideTab = () => {
  const [currentTab, setCurrentTab] = useState('search'); // 'search' | 'trips' | 'messages'
  const [booting, setBooting] = useState(true);
  const [error, setError] = useState('');
  const [externalPickup, setExternalPickup] = useState(null);
  const [externalDropoff, setExternalDropoff] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [mapPickerTarget, setMapPickerTarget] = useState(null);
  const [savedPlaces, setSavedPlaces] = useState(readSavedPlaces);
  const [savedPlacesOpen, setSavedPlacesOpen] = useState(false);
  const [savingPlace, setSavingPlace] = useState(null);
  const [scheduledRideDraft, setScheduledRideDraft] = useState(null);
  const [tripsRefreshToken, setTripsRefreshToken] = useState(0);
  const [tripsSection, setTripsSection] = useState('BOOKED');
  const currentLocationAccuracyRef = React.useRef(Number.POSITIVE_INFINITY);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const resolveCurrentLocation = async ({ latitude, longitude, accuracy }) => {
    // Mirror RideLocationService in the Flutter app: GPS first, then a
    // best-effort reverse-geocode.  Administrative codes remain null because
    // a device coordinate is not a confirmed corridor commune selection.
    if (Number.isFinite(Number(accuracy)) && Number(accuracy) >= currentLocationAccuracyRef.current) return;
    currentLocationAccuracyRef.current = Number.isFinite(Number(accuracy))
      ? Number(accuracy) : currentLocationAccuracyRef.current;
    const basePoint = {
      address: 'Khu vực hiện tại',
      latitude,
      longitude,
      provinceCode: null,
      communeCode: null,
      lowAccuracy: Number(accuracy) > 100,
    };
    setExternalPickup(basePoint);
    try {
      const response = await customerRideApi.reverseGeocode({ lat: latitude, lng: longitude });
      const place = response?.results?.[0] || response?.data?.results?.[0] || {};
      const address = place.display || place.address || basePoint.address;
      setExternalPickup({ ...basePoint, address });
    } catch (_) {
      // The GPS coordinate is still valid for the search payload; only the
      // human-readable label falls back, just as the app does.
    }
  };

  useEffect(() => {
    let active = true;
    customerRideApi.getStatus()
      .then(() => {
        if (!active) return;
      })
      .catch((requestError) => {
        if (active) setError(rideErrorMessage(requestError, 'Dịch vụ Xe ghép hiện chưa sẵn sàng.'));
      })
      .finally(() => {
        if (active) setBooting(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const persistSavedPlaces = (nextPlaces) => {
    setSavedPlaces(nextPlaces);
    window.localStorage.setItem(SAVED_PLACES_KEY, JSON.stringify(nextPlaces));
  };

  const savePlace = (placeSpec, point) => {
    const nextPlaces = [
      { id: placeSpec.id, label: placeSpec.label, point },
      ...savedPlaces.filter((place) => place.id !== placeSpec.id),
    ];
    persistSavedPlaces(nextPlaces);
    setSavingPlace(null);
    showToast(`Đã lưu ${placeSpec.label}.`);
  };

  const startSavePlace = (id, label) => {
    setSavedPlacesOpen(false);
    setSavingPlace({ id, label });
  };

  const selectSavedPlace = (place) => {
    setExternalDropoff(place.point);
    setSavedPlacesOpen(false);
    showToast(`Đã chọn điểm đến: ${place.label}.`);
  };

  const mapPickerConfig = typeof mapPickerTarget === 'string'
    ? { target: mapPickerTarget }
    : mapPickerTarget;

  const homePlace = savedPlaces.find((place) => place.id === 'home');
  const workPlace = savedPlaces.find((place) => place.id === 'work');
  const customSavedPlaces = savedPlaces.filter((place) => place.id !== 'home' && place.id !== 'work');

  const breadcrumbLabels = {
    search: 'Tìm chuyến',
    trips: 'Chuyến đi của tôi',
    messages: 'Tin nhắn',
  };

  if (booting) {
    return (
      <div className="customer-ride-portal-wrapper" data-customer-ride>
        <div className="customer-ride-loading">
          <RideAppIcon name="time" className="ride-icon-spin" /> Đang kết nối dịch vụ Xe ghép...
        </div>
      </div>
    );
  }

  return (
    <div className="customer-ride-portal-wrapper" data-customer-ride>
      {/* Subnav switcher matching sidebar subnav in evg_xe_ghep_khach_hang.html */}
      <nav className="portal-subnav-bar" aria-label="Điều hướng phân hệ Xe ghép">
        <button
          type="button"
          className={`subnav-tab-btn ${currentTab === 'search' ? 'active' : ''}`}
          onClick={() => {
            setCurrentTab('search');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <RideAppIcon name="search" /> Tìm chuyến
        </button>

        <button
          type="button"
          className={`subnav-tab-btn ${currentTab === 'trips' ? 'active' : ''}`}
          onClick={() => {
            setCurrentTab('trips');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <RideAppIcon name="calendar" /> Chuyến đi của tôi
        </button>

        <button
          type="button"
          className={`subnav-tab-btn ${currentTab === 'messages' ? 'active' : ''}`}
          onClick={() => {
            setCurrentTab('messages');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <RideAppIcon name="message" /> Tin nhắn
        </button>
      </nav>

      {/* Breadcrumb matching evg_xe_ghep_khach_hang.html */}
      <nav className="bread" aria-label="Đường dẫn trang">
        <a href="/customer/home">Trang chủ</a>
        <span aria-hidden="true">›</span>
        <a
          href="/customer/ride"
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            setCurrentTab('search');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          Xe ghép
        </a>
        <span aria-hidden="true">›</span>
        <b id="breadcrumb" aria-current="page">{breadcrumbLabels[currentTab] || 'Tìm chuyến'}</b>
      </nav>

      {/* Global alert if error */}
      {error && (
        <div className="customer-ride-alert danger">
          <RideAppIcon name="warning" /> {error}
        </div>
      )}

      {/* ------------------------------------------------------------------
          SECTION 1: SEARCH (#search)
          ------------------------------------------------------------------ */}
      {currentTab === 'search' && (
        <section id="search" className="section active">
          {/* Page Header Card matching prototype */}
          <div className="page-header">
            <div>
              <h1>Xe ghép</h1>
              <p>Tìm và đặt chuyến xe ghép nhanh chóng, tiết kiệm và thuận tiện.</p>
            </div>
            <button
              type="button"
              className="outline"
              onClick={() => {
                setCurrentTab('trips');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <RideAppIcon name="calendar" /> Chuyến đi của tôi
            </button>
          </div>

          {/* Search Layout (Left Search Card + Right Map Mockup) */}
          <div className="search-layout">
            {/* Left: Search Card */}
            <RideSearchSection
              loading={false}
              onScheduleRide={setScheduledRideDraft}
              externalPickup={externalPickup}
              externalDropoff={externalDropoff}
              onOpenMapPicker={setMapPickerTarget}
            />

            <RideHomeMap
              onCurrentLocation={resolveCurrentLocation}
              onPickFromMap={() => setMapPickerTarget('DROPOFF')}
            />
          </div>

          {savingPlace && <RidePlaceSearchSheet
            target="DROPOFF"
            currentLocation={externalPickup}
            value={null}
            onClose={() => setSavingPlace(null)}
            onOpenMap={() => {
              setMapPickerTarget({ target: 'DROPOFF', saveSpec: savingPlace });
              setSavingPlace(null);
            }}
            onSelect={(point) => savePlace(savingPlace, point)}
          />}

          {mapPickerConfig && <RideMapPickerSheet
            initialPoint={mapPickerConfig.target === 'PICKUP'
              ? externalPickup
              : (externalDropoff || externalPickup)}
            target={mapPickerConfig.target}
            onClose={() => setMapPickerTarget(null)}
            onConfirm={(point) => {
              if (mapPickerConfig.saveSpec) savePlace(mapPickerConfig.saveSpec, point);
              else if (mapPickerConfig.target === 'PICKUP') setExternalPickup(point);
              else setExternalDropoff(point);
              setMapPickerTarget(null);
            }}
          />}

          {savedPlacesOpen && <RideSavedPlacesSheet
            places={savedPlaces}
            onClose={() => setSavedPlacesOpen(false)}
            onSelect={selectSavedPlace}
            onDelete={(id) => {
              persistSavedPlaces(savedPlaces.filter((place) => place.id !== id));
              showToast('Đã xoá địa điểm đã lưu.');
            }}
            onRename={(id, label) => {
              persistSavedPlaces(savedPlaces.map((place) => (place.id === id ? { ...place, label } : place)));
              showToast('Đã đổi tên địa điểm.');
            }}
            onAdd={(label) => startSavePlace(`place-${Date.now()}`, label)}
          />}

          {scheduledRideDraft && <RideScheduledDemandModal
            initial={scheduledRideDraft}
            onClose={() => setScheduledRideDraft(null)}
            onCreated={() => {
              setScheduledRideDraft(null);
              setTripsRefreshToken((value) => value + 1);
              setTripsSection('MINE');
              setCurrentTab('trips');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              showToast('Đã gửi yêu cầu chuyến đi hẹn giờ tới tài xế.');
            }}
          />}

          {/* Saved Shortcuts Row matching prototype */}
          <div className="saved">
            <button
              type="button"
              onClick={() => (homePlace ? selectSavedPlace(homePlace) : startSavePlace('home', 'Nhà'))}
            >
              <i className="fa-regular fa-house icon-wrap" />
              <span>
                <b>Nhà</b>
                <small>{homePlace?.point.address || 'Thêm nhà'}</small>
              </span>
              <span className="forward">›</span>
            </button>

            <button
              type="button"
              onClick={() => (workPlace ? selectSavedPlace(workPlace) : startSavePlace('work', 'Công ty'))}
            >
              <i className="fa-regular fa-briefcase icon-wrap" />
              <span>
                <b>Công ty</b>
                <small>{workPlace?.point.address || 'Thêm công ty'}</small>
              </span>
              <span className="forward">›</span>
            </button>

            <button
              type="button"
              onClick={() => setSavedPlacesOpen(true)}
            >
              <i className="fa-regular fa-star icon-wrap" />
              <span>
                <b>Địa điểm đã lưu</b>
                <small>{customSavedPlaces.length ? `${customSavedPlaces.length} địa điểm` : 'Chưa có'}</small>
              </span>
              <span className="forward">›</span>
            </button>
          </div>

          {/* Chuyến gần đây Section matching prototype */}
          <div className="mini-title">
            Chuyến gần đây{' '}
            <a
              onClick={() => {
                setCurrentTab('trips');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Xem tất cả ›
            </a>
          </div>

          <div className="recentgrid">
            <button
              type="button"
              className="recent"
              onClick={() => {
                setExternalDropoff(RECENT_GIAO_THUY_DROPOFF);
                showToast('Đã chọn điểm đến: Huyện Giao Thủy');
              }}
            >
              <span className="round">
                <i className="fa-regular fa-clock-rotate-left" />
              </span>
              <span>
                <b>Huyện Giao Thủy</b>
                <small>Tỉnh Ninh Bình, Việt Nam</small>
              </span>
              <span className="forward">›</span>
            </button>

            <button
              type="button"
              className="recent"
              onClick={() => {
                setExternalDropoff({
                  address: 'Hà Nội, Việt Nam',
                  provinceCode: '01',
                  communeCode: '10105001',
                  latitude: 21.0285,
                  longitude: 105.8542,
                });
                showToast('Đã chọn điểm đến: Hà Nội');
              }}
            >
              <span className="round">
                <i className="fa-regular fa-clock-rotate-left" />
              </span>
              <span>
                <b>Hà Nội</b>
                <small>Việt Nam</small>
              </span>
              <span className="forward">›</span>
            </button>
          </div>

          <p className="footnote">Điểm đón và điểm đến được xác thực qua dịch vụ địa chỉ trước khi tìm chuyến.</p>
        </section>
      )}

      {/* ------------------------------------------------------------------
          SECTION 2: TRIPS (#trips)
          ------------------------------------------------------------------ */}
      {currentTab === 'trips' && (
        <MyRideBookings
          refreshToken={tripsRefreshToken}
          initialSection={tripsSection}
          onFindTrips={() => {
            setCurrentTab('search');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}

      {/* ------------------------------------------------------------------
          SECTION 3: MESSAGES (#messages)
          ------------------------------------------------------------------ */}
      {currentTab === 'messages' && <RideMessagesView />}

      {/* Toast Notification */}
      {toastMessage && <div className="toast show">{toastMessage}</div>}

    </div>
  );
};

export default CustomerRideTab;
